import { useState } from 'react';
import { useParams, Link } from '@tanstack/react-router';
import { INACTIVE_DAYS, daysSince } from '@/lib/tutor/dashboard-overview';
import {
  ArrowLeft,
  Copy,
  Check,
  UserMinus,
  Users,
  Flame,
  Plus,
  AlertCircle,
  CheckCircle2,
  CalendarRange,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import { useAuth } from '@/lib/auth';
import { useTutorClassDetail, useMyExercises } from '@/lib/tutor/queries';
import { useRemoveClassMember } from '@/lib/tutor/mutations';
import { useAssignExerciseToClass } from '@/lib/tutor/assignment-mutations';
import { DocumentsPanel } from '@/components/documents/documents-panel';
import type { ClassMember } from '@/lib/tutor/queries';
import { ExercisePickerField, toExercisePickerItem } from '@/components/pickers/exercise-picker';

const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));
const SUBJECT_LABEL = Object.fromEntries(SUBJECTS.map((s) => [s.value, s.label]));

export function TutorClassDetailPage() {
  const { classId } = useParams({ strict: false }) as { classId: string };
  const { user } = useAuth();
  const tutorId = user?.id ?? '';
  const { data: cls, isLoading } = useTutorClassDetail(classId);
  const { data: exercises } = useMyExercises(tutorId);
  const removeMemeber = useRemoveClassMember();
  const assignToClass = useAssignExerciseToClass();
  const [copied, setCopied] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const [assignExerciseId, setAssignExerciseId] = useState('');
  const [assignDueAt, setAssignDueAt] = useState('');
  const [assignNote, setAssignNote] = useState('');
  const [assignSuccess, setAssignSuccess] = useState<number | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);

  const publishedExercises = (exercises ?? []).filter((e) => e.status === 'published');

  async function handleAssignToClass(ev: React.FormEvent) {
    ev.preventDefault();
    if (!assignExerciseId) return;
    setAssignError(null);
    try {
      const result = await assignToClass.mutateAsync({
        classId,
        exerciseId: assignExerciseId,
        dueAt: assignDueAt || null,
        note: assignNote || null,
      });
      setAssignExerciseId('');
      setAssignDueAt('');
      setAssignNote('');
      setAssignSuccess(result.count);
      setTimeout(() => setAssignSuccess(null), 3000);
    } catch (err) {
      setAssignError(err instanceof Error ? err.message : "Erreur lors de l'assignation");
    }
  }

  async function copyCode() {
    if (!cls) return;
    await navigator.clipboard.writeText(cls.invite_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleRemove(member: ClassMember) {
    if (!confirm(`Retirer ${member.display_name} de la classe ?`)) return;
    setRemovingId(member.member_id);
    try {
      await removeMemeber.mutateAsync({ tutorId, classId, memberId: member.member_id });
    } finally {
      setRemovingId(null);
    }
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl p-6 md:p-8">
        <div className="bg-ink-100 mb-6 h-6 w-32 animate-pulse rounded-lg" />
        <div className="bg-ink-100 h-32 animate-pulse rounded-2xl" />
      </div>
    );
  }

  if (!cls) {
    return (
      <div className="mx-auto max-w-4xl p-6 md:p-8">
        <Link
          to="/tutor/classes"
          className="text-ink-500 hover:text-ink-900 mb-6 inline-flex items-center gap-1.5 text-sm"
        >
          <ArrowLeft className="h-4 w-4" /> Mes classes
        </Link>
        <p className="text-ink-500 font-sans text-sm">Classe introuvable.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl p-6 md:p-8">
      {/* Back */}
      <Link
        to="/tutor/classes"
        className="font-display text-ink-500 hover:text-ink-900 mb-6 inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Mes classes
      </Link>

      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mb-1.5 flex items-center gap-2">
            {cls.level && (
              <span className="bg-primary-100 text-primary-700 font-display inline-flex h-6 rounded-full px-2.5 text-[11px] font-bold uppercase tracking-[.06em]">
                {LEVEL_LABEL[cls.level as SchoolLevel] ?? cls.level}
              </span>
            )}
            {cls.subject && (
              <span className="font-display text-ink-500 text-[11px] font-semibold uppercase tracking-[.1em]">
                {SUBJECT_LABEL[cls.subject as SubjectSlug] ?? cls.subject}
              </span>
            )}
          </div>
          <h1 className="font-display text-ink-900 text-2xl font-extrabold tracking-tight md:text-[26px]">
            {cls.name}
          </h1>
          {cls.description && (
            <p className="text-ink-500 mt-1 font-sans text-sm">{cls.description}</p>
          )}
        </div>

        {/* Invite code */}
        <div className="border-ink-200 flex items-center gap-2 rounded-xl border bg-white px-4 py-2.5 shadow-sm">
          <span className="text-ink-500 font-sans text-xs">Code :</span>
          <span className="text-ink-900 font-mono text-sm font-semibold">{cls.invite_code}</span>
          <button
            onClick={copyCode}
            className="hover:bg-ink-100 text-ink-500 ml-1 rounded-lg p-1 transition-colors"
            title="Copier le code"
          >
            {copied ? <Check className="text-xp-500 h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Progression de l'année (lot F) */}
      {cls.level && (
        <Link
          to="/tutor/classes/$classId/progression"
          params={{ classId: cls.id }}
          className="border-ink-200 hover:border-primary-300 mb-6 flex min-h-[2.75rem] items-center justify-between gap-3 rounded-2xl border bg-white px-4 py-3"
        >
          <span className="flex items-center gap-2">
            <CalendarRange className="text-primary-500 h-5 w-5" aria-hidden />
            <span>
              <span className="font-display text-ink-900 block text-sm font-bold">
                Progression de l’année
              </span>
              <span className="text-ink-500 block text-xs">
                Packs de SKILLCARDS, ordre de la classe, maîtrise de chaque élève
              </span>
            </span>
          </span>
          <ChevronRight className="text-ink-400 h-5 w-5" aria-hidden />
        </Link>
      )}

      {/* Members section */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-ink-500 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[.1em]">
            <Users className="h-4 w-4" />
            Élèves ({cls.member_count})
          </h2>
        </div>

        {cls.members.length === 0 ? (
          <div className="border-ink-200 rounded-2xl border bg-white p-8 text-center">
            <p className="font-display text-ink-700 mb-1 font-bold">Aucun élève</p>
            <p className="text-ink-500 font-sans text-sm">
              Donne le code{' '}
              <span className="text-ink-900 font-mono font-semibold">{cls.invite_code}</span> à tes
              élèves pour qu'ils rejoignent.
            </p>
          </div>
        ) : (
          <div className="border-ink-200 overflow-hidden rounded-2xl border bg-white">
            {/* Table header */}
            <div className="border-ink-100 bg-ink-50 hidden grid-cols-[2fr_1fr_1fr_1fr_52px] border-b px-5 py-3 sm:grid">
              {['Élève', 'XP', 'Série', 'Activité', ''].map((h) => (
                <span
                  key={h}
                  className="font-display text-ink-500 text-[11px] font-bold uppercase tracking-[.08em]"
                >
                  {h}
                </span>
              ))}
            </div>

            {cls.members.map((member, i) => (
              <MemberRow
                key={member.member_id}
                member={member}
                last={i === cls.members.length - 1}
                removing={removingId === member.member_id}
                onRemove={() => handleRemove(member)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Fichiers de la classe (migration 0261) — le tuteur dépose, les élèves membres lisent */}
      <section className="mt-6">
        <h2 className="font-display text-ink-500 mb-3 text-[11px] font-bold uppercase tracking-[.1em]">
          Fichiers de la classe
        </h2>
        <DocumentsPanel
          target={{ kind: 'class', classId }}
          canUpload
          emptyLabel="Aucun fichier partagé avec cette classe."
        />
      </section>

      {/* Assign exercise to class */}
      <section className="mt-6">
        <h2 className="font-display text-ink-500 mb-3 text-[11px] font-bold uppercase tracking-[.1em]">
          Assigner un exercice à toute la classe
        </h2>
        <form
          onSubmit={handleAssignToClass}
          className="border-ink-200 space-y-3 rounded-2xl border bg-white p-5"
        >
          <ExercisePickerField
            label="Exercice *"
            items={publishedExercises.map(toExercisePickerItem)}
            value={assignExerciseId || null}
            onChange={setAssignExerciseId}
            title="Choisir l’exercice à assigner"
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="font-display text-ink-700 mb-1.5 block text-xs font-semibold">
                Date limite <span className="text-ink-400 font-normal">(optionnel)</span>
              </label>
              <input
                type="date"
                value={assignDueAt}
                onChange={(e) => setAssignDueAt(e.target.value)}
                className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-400 h-11 w-full rounded-xl border px-3 font-sans text-sm focus:outline-none"
              />
            </div>
            <div>
              <label className="font-display text-ink-700 mb-1.5 block text-xs font-semibold">
                Note <span className="text-ink-400 font-normal">(optionnel)</span>
              </label>
              <input
                type="text"
                value={assignNote}
                onChange={(e) => setAssignNote(e.target.value)}
                maxLength={200}
                placeholder="Message pour les élèves…"
                className="border-ink-200 bg-ink-50 text-ink-900 placeholder:text-ink-400 focus:border-primary-400 h-11 w-full rounded-xl border px-3 font-sans text-sm focus:outline-none"
              />
            </div>
          </div>
          {assignError && (
            <div className="text-error-700 bg-error-50 border-error-100 flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {assignError}
            </div>
          )}
          {assignSuccess !== null && (
            <div className="text-xp-700 bg-xp-50 border-xp-100 flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              Exercice assigné à {assignSuccess} élève{assignSuccess !== 1 ? 's' : ''} !
            </div>
          )}
          <button
            type="submit"
            disabled={!assignExerciseId || assignToClass.isPending || cls.member_count === 0}
            className="bg-primary-400 font-display hover:bg-primary-500 inline-flex min-h-[2.75rem] w-full items-center gap-2 rounded-xl px-5 text-sm font-bold text-white transition-all active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          >
            <Plus className="h-4 w-4" />
            {assignToClass.isPending
              ? 'Assignation…'
              : `Assigner à la classe (${cls.member_count} élève${cls.member_count !== 1 ? 's' : ''})`}
          </button>
        </form>
      </section>
    </div>
  );
}

function MemberRow({
  member,
  last,
  removing,
  onRemove,
}: {
  member: ClassMember;
  last: boolean;
  removing: boolean;
  onRemove: () => void;
}) {
  const initials = member.display_name
    .split(' ')
    .map((n) => n[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');
  const days = daysSince(member.last_activity_at, Date.now());
  const inactive = days === null || days >= INACTIVE_DAYS;
  const activity =
    days === null ? 'Jamais actif' : days === 0 ? 'Actif aujourd’hui' : `Il y a ${days} j`;

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-4 py-3 sm:grid sm:grid-cols-[2fr_1fr_1fr_1fr_52px] sm:gap-0 sm:px-5',
        !last && 'border-ink-100 border-b',
        removing && 'opacity-50',
      )}
    >
      {/* Élève (+ résumé sur mobile, où les colonnes sont masquées) */}
      <Link
        to="/tutor/students/$studentId"
        params={{ studentId: member.student_id }}
        className="hover:text-primary-700 flex min-h-[2.75rem] min-w-0 flex-1 items-center gap-2.5 transition-colors"
      >
        <div className="bg-ink-100 text-ink-700 font-display flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold">
          {initials}
        </div>
        <span className="min-w-0">
          <span className="font-display text-ink-900 block truncate text-sm font-bold">
            {member.display_name}
          </span>
          <span
            className={cn(
              'block font-sans text-xs sm:hidden',
              inactive ? 'text-amber-700' : 'text-ink-500',
            )}
          >
            {member.xp} XP · série {member.streak_days} j · {activity}
          </span>
        </span>
      </Link>

      <span className="text-ink-900 hidden font-mono text-sm sm:block">{member.xp} XP</span>
      <div className="hidden items-center gap-1 text-sm sm:flex">
        <Flame className="text-accent-400 h-3.5 w-3.5 shrink-0" />
        <span className="text-ink-700 font-mono">{member.streak_days} j</span>
      </div>
      <span
        className={cn(
          'hidden font-sans text-sm sm:block',
          inactive ? 'font-semibold text-amber-700' : 'text-ink-600',
        )}
      >
        {activity}
      </span>

      <div className="flex shrink-0 justify-end">
        <button
          onClick={onRemove}
          disabled={removing}
          className="text-ink-400 hover:text-error-500 hover:bg-error-50 flex h-11 w-11 items-center justify-center rounded-lg transition-colors"
          aria-label={`Retirer ${member.display_name} de la classe`}
        >
          <UserMinus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
